import type Phaser from 'phaser';
import { CELL_W, PIXEL_FONT } from '../ui/pixelFont';
import { FlickDetector, STICK_RADIUS, classifySwipe, dragStick, stickValue } from './gestures';

type Role =
  | { kind: 'stick' }
  | { kind: 'jump' }
  | { kind: 'fire' }
  | { kind: 'swipe'; x: number; y: number; at: number; done: boolean };

const JUMP_R = 26;
const FIRE_R = 19;
/** Controls fade to this after a few seconds untouched (GDD: Controls and UX). */
const IDLE_ALPHA = 0.3;
const IDLE_AFTER_MS = 3000;
const DEPTH = 1000;

/**
 * Two-thumb landscape controls: a floating stick anywhere on the left, JUMP and FIRE buttons on the
 * right, and lane swipes on the rest of the right side. Reads touches only; the input controller
 * turns these values into intents.
 */
export class TouchControls {
  moveX = 0;
  jumpHeld = false;
  fireHeld = false;
  /** Presses since the last sample, so a tap shorter than one tick still counts. */
  jumpTapped = false;
  fireTapped = false;
  /** A double flick of the stick since the last sample. */
  dashTapped = false;
  /** Set on the first touch: the controls are drawn only on touch screens. */
  used = false;

  private readonly roles = new Map<number, Role>();
  private readonly lanes: (-1 | 1)[] = [];
  private readonly flicks = new FlickDetector();
  private stickX = 0;
  private stickY = 0;
  private thumbX = 0;
  private thumbY = 0;
  private lastTouchAt = 0;
  /** Off while the game is paused: touches then belong to the pause menu. */
  private enabled = true;
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly labels: Phaser.GameObjects.BitmapText[];
  private readonly jumpLabel: Phaser.GameObjects.BitmapText;
  private readonly fireLabel: Phaser.GameObjects.BitmapText;
  private readonly laneLabel: Phaser.GameObjects.BitmapText;

  constructor(private readonly scene: Phaser.Scene) {
    this.gfx = scene.add.graphics().setDepth(DEPTH);
    this.jumpLabel = this.label('JUMP');
    this.fireLabel = this.label('FIRE');
    this.laneLabel = this.label('SWIPE\nLANE');
    this.labels = [this.jumpLabel, this.fireLabel, this.laneLabel];
    this.used = scene.sys.game.device.input.touch;

    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.down(p));
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => this.move(p));
    scene.input.on('pointerup', (p: Phaser.Input.Pointer) => this.up(p));
    scene.input.on('pointerupoutside', (p: Phaser.Input.Pointer) => this.up(p));
  }

  /** Turns the controls on or off, letting go of every finger and dropping queued presses. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    this.roles.clear();
    this.lanes.length = 0;
    this.moveX = 0;
    this.flicks.update(0, this.scene.time.now);
    this.jumpHeld = this.fireHeld = false;
    this.jumpTapped = this.fireTapped = this.dashTapped = false;
  }

  /** The next lane swipe, if any (one per sim tick). */
  takeLane(): -1 | 0 | 1 {
    return this.lanes.shift() ?? 0;
  }

  private label(text: string): Phaser.GameObjects.BitmapText {
    return this.scene.add
      .bitmapText(0, 0, PIXEL_FONT, text, CELL_W)
      .setOrigin(0.5)
      .setCenterAlign()
      .setDepth(DEPTH + 1);
  }

  private get width(): number {
    return this.scene.scale.width;
  }

  private get height(): number {
    return this.scene.scale.height;
  }

  private jumpPos() {
    return { x: this.width - 42, y: this.height - 44 };
  }

  private firePos() {
    return { x: this.width - 100, y: this.height - 30 };
  }

  private down(p: Phaser.Input.Pointer): void {
    if (!p.wasTouch || !this.enabled) return;
    this.used = true;
    this.lastTouchAt = this.scene.time.now;
    const jump = this.jumpPos();
    const fire = this.firePos();
    if (p.x < this.width * 0.45) {
      if ([...this.roles.values()].some((r) => r.kind === 'stick')) return;
      this.roles.set(p.id, { kind: 'stick' });
      this.stickX = this.thumbX = p.x;
      this.stickY = this.thumbY = p.y;
    } else if (Math.hypot(p.x - jump.x, p.y - jump.y) <= JUMP_R + 8) {
      this.roles.set(p.id, { kind: 'jump' });
      this.jumpHeld = true;
      this.jumpTapped = true;
    } else if (Math.hypot(p.x - fire.x, p.y - fire.y) <= FIRE_R + 8) {
      this.roles.set(p.id, { kind: 'fire' });
      this.fireHeld = true;
      this.fireTapped = true;
    } else {
      this.roles.set(p.id, { kind: 'swipe', x: p.x, y: p.y, at: this.scene.time.now, done: false });
    }
  }

  private move(p: Phaser.Input.Pointer): void {
    const role = this.roles.get(p.id);
    if (!role) return;
    this.lastTouchAt = this.scene.time.now;
    if (role.kind === 'stick') {
      const c = dragStick(this.stickX, this.stickY, p.x, p.y);
      this.stickX = c.cx;
      this.stickY = c.cy;
      this.thumbX = p.x;
      this.thumbY = p.y;
      this.moveX = stickValue(p.x - this.stickX);
      if (this.flicks.update(this.moveX, this.scene.time.now)) this.dashTapped = true;
    } else if (role.kind === 'swipe' && !role.done) {
      const lane = classifySwipe(p.x - role.x, p.y - role.y, this.scene.time.now - role.at);
      if (lane !== 0) {
        role.done = true;
        this.lanes.push(lane);
      }
    }
  }

  private up(p: Phaser.Input.Pointer): void {
    const role = this.roles.get(p.id);
    if (!role) return;
    this.roles.delete(p.id);
    if (role.kind === 'stick') {
      this.moveX = 0;
      this.flicks.update(0, this.scene.time.now);
    } else if (role.kind === 'jump') this.jumpHeld = false;
    else if (role.kind === 'fire') this.fireHeld = false;
  }

  /** Redraws the controls (cheap: a handful of circles). */
  draw(): void {
    const g = this.gfx;
    g.clear();
    for (const l of this.labels) l.setVisible(this.used);
    if (!this.used) return;

    const idle = this.scene.time.now - this.lastTouchAt > IDLE_AFTER_MS && this.roles.size === 0;
    const a = idle ? IDLE_ALPHA : 1;
    const stickActive = [...this.roles.values()].some((r) => r.kind === 'stick');

    // Stick: under the thumb when held, a faint hint where it usually sits when not.
    const sx = stickActive ? this.stickX : 64;
    const sy = stickActive ? this.stickY : this.height - 52;
    g.lineStyle(2, 0xffffff, (stickActive ? 0.5 : 0.18) * a).strokeCircle(sx, sy, STICK_RADIUS);
    const kx = stickActive
      ? sx + Math.max(-STICK_RADIUS, Math.min(STICK_RADIUS, this.thumbX - sx))
      : sx;
    const ky = stickActive
      ? sy + Math.max(-STICK_RADIUS, Math.min(STICK_RADIUS, this.thumbY - sy))
      : sy;
    g.fillStyle(0xffffff, (stickActive ? 0.55 : 0.18) * a).fillCircle(kx, ky, 9);

    const jump = this.jumpPos();
    const fire = this.firePos();
    g.fillStyle(0xffd23f, (this.jumpHeld ? 0.7 : 0.35) * a).fillCircle(jump.x, jump.y, JUMP_R);
    g.lineStyle(2, 0x20184a, 0.8 * a).strokeCircle(jump.x, jump.y, JUMP_R);
    g.fillStyle(0xff5a4a, (this.fireHeld ? 0.7 : 0.35) * a).fillCircle(fire.x, fire.y, FIRE_R);
    g.lineStyle(2, 0x20184a, 0.8 * a).strokeCircle(fire.x, fire.y, FIRE_R);
    this.jumpLabel.setPosition(jump.x, jump.y).setAlpha(a);
    this.fireLabel.setPosition(fire.x, fire.y).setAlpha(a);

    // Swipe zone hint: chevrons up and down above the buttons.
    const hx = this.width - 70;
    const hy = this.height - 132;
    g.lineStyle(2, 0xffffff, 0.25 * a);
    g.lineBetween(hx - 6, hy - 10, hx, hy - 16).lineBetween(hx, hy - 16, hx + 6, hy - 10);
    g.lineBetween(hx - 6, hy + 10, hx, hy + 16).lineBetween(hx, hy + 16, hx + 6, hy + 10);
    this.laneLabel.setPosition(hx, hy).setAlpha(0.5 * a);
  }
}
